import React from 'react';

import {
	UPTIME_INCIDENTS_SHOWN,
	dailySummary,
	formatIncidentStart,
	formatMinutes,
	formatPercent,
	formatUtcDay,
	uptimeLevel,
} from '../../utils/status';

/**
 * One status row's uptime (KPR-758): its 30- and 90-day figures, a bar of the
 * last 90 UTC days, and its latest incidents. Measured from outside keepr by
 * Route 53 health checks and handed over by GET /api/status (`uptime`); this
 * draws an entry `normalizeUptime` has already checked, and reads nothing.
 *
 * The words carry the meaning and the bar is a picture of them: each day's
 * color (status tokens: no downtime, some, most of the day, not measured) has
 * a hover title, and the bar as a whole reads as one sentence to a screen
 * reader. On a phone the bar shows the last 30 days, so a day stays wider than
 * a hairline (public-site.css).
 */
export default function StatusUptime({ uptime }) {
	if (!uptime) return null;
	const { days30, days90, daily, incidents } = uptime;
	const measured = days30 !== null || days90 !== null || incidents.length > 0;
	if (!measured) {
		return <p className="status-uptime status-uptime__note">Uptime appears here once keepr has measured it for a day.</p>;
	}
	const shown = incidents.slice(0, UPTIME_INCIDENTS_SHOWN);
	return (
		<div className="status-uptime">
			<p className="status-uptime__figures">
				<span>
					<strong>{days30 === null ? 'Not measured' : formatPercent(days30)}</strong> uptime, last 30 days
				</span>
				<span>
					<strong>{days90 === null ? 'Not measured' : formatPercent(days90)}</strong> last 90 days
				</span>
			</p>
			{daily.length ? (
				<>
					<div className="status-uptime__bar" role="img" aria-label={dailySummary(daily)}>
						{daily.map(day => (
							<span
								key={day.date}
								className={`status-uptime__day status-uptime__day--${uptimeLevel(day.percent)}`}
								title={`${formatUtcDay(day.date)} (UTC): ${day.percent === null ? 'not measured' : `${formatPercent(day.percent)} up`}`}
							/>
						))}
					</div>
					<div className="status-uptime__axis" aria-hidden="true">
						<span className="status-uptime__axis-start status-uptime__axis-start--long">{daily.length} days ago</span>
						<span className="status-uptime__axis-start status-uptime__axis-start--short">{Math.min(30, daily.length)} days ago</span>
						<span>Today</span>
					</div>
				</>
			) : null}
			{shown.length ? (
				<div className="status-uptime__incidents">
					<p className="status-uptime__label">Recent incidents</p>
					<ul>
						{shown.map(incident => (
							<li key={incident.start}>
								<time dateTime={incident.start}>{formatIncidentStart(incident.start)}</time>
								<span>down for {formatMinutes(incident.durationMinutes)}</span>
							</li>
						))}
					</ul>
				</div>
			) : (
				<p className="status-uptime__note">No incidents recorded in the last 63 days.</p>
			)}
		</div>
	);
}
